import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import { getCustomFoods, createCustomFood, updateCustomFood, deleteCustomFood } from '../utils/nutritionApi';

const emptyForm = {
  food_name: '',
  calories: '',
  protein: '',
  carbs: '',
  fat: '',
  fiber: '',
  serving_size: '',
  serving_unit: ''
};

function CustomFoods() {
  const navigate = useNavigate();
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchFoods = useCallback(async () => {
    try {
      const data = await getCustomFoods();
      setFoods(data);
    } catch (err) {
      console.error('Failed to load custom foods:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFoods();
  }, [fetchFoods]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (food) => {
    setForm({
      food_name: food.food_name,
      calories: food.calories,
      protein: food.protein || '',
      carbs: food.carbs || '',
      fat: food.fat || '',
      fiber: food.fiber || '',
      serving_size: food.serving_size || '',
      serving_unit: food.serving_unit || ''
    });
    setEditingId(food.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.food_name || !form.calories) {
      setToast({ message: 'Name and calories are required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        food_name: form.food_name,
        calories: parseInt(form.calories),
        protein: form.protein ? parseFloat(form.protein) : null,
        carbs: form.carbs ? parseFloat(form.carbs) : null,
        fat: form.fat ? parseFloat(form.fat) : null,
        fiber: form.fiber ? parseFloat(form.fiber) : null,
        serving_size: form.serving_size ? parseFloat(form.serving_size) : null,
        serving_unit: form.serving_unit || null
      };

      if (editingId) {
        await updateCustomFood(editingId, payload);
        setToast({ message: 'Food updated', type: 'success' });
      } else {
        await createCustomFood(payload);
        setToast({ message: 'Food created', type: 'success' });
      }
      resetForm();
      fetchFoods();
    } catch (err) {
      console.error('Save failed:', err.message);
      setToast({ message: 'Failed to save food', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteCustomFood(id);
      setToast({ message: 'Food deleted', type: 'success' });
      fetchFoods();
    } catch (err) {
      console.error('Delete failed:', err.message);
      setToast({ message: 'Failed to delete food', type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="page custom-foods-page">
      <div className="custom-foods-header">
        <button className="back-link" onClick={() => navigate('/settings')}>&larr; Settings</button>
        <h2>Custom Foods</h2>
      </div>

      <button
        className="btn btn-primary btn-full"
        onClick={() => { resetForm(); setShowForm(true); }}
        style={{ marginBottom: '1rem' }}
      >
        + Create Custom Food
      </button>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="card custom-foods-form">
          <div className="custom-foods-form-header">
            <h3>{editingId ? 'Edit Food' : 'New Custom Food'}</h3>
            <button className="copy-panel-close" onClick={resetForm}>&times;</button>
          </div>

          <div className="form-group">
            <label>Food Name *</label>
            <input
              type="text"
              value={form.food_name}
              onChange={(e) => setField('food_name', e.target.value)}
              placeholder="e.g. Homemade Granola"
            />
          </div>

          <div className="form-group">
            <label>Calories *</label>
            <input
              type="number"
              value={form.calories}
              onChange={(e) => setField('calories', e.target.value)}
              placeholder="0"
              min="0"
            />
          </div>

          <div className="settings-macro-row">
            <div className="form-group">
              <label>Protein (g)</label>
              <input
                type="number"
                value={form.protein}
                onChange={(e) => setField('protein', e.target.value)}
                placeholder="--"
                min="0"
                step="0.1"
              />
            </div>
            <div className="form-group">
              <label>Carbs (g)</label>
              <input
                type="number"
                value={form.carbs}
                onChange={(e) => setField('carbs', e.target.value)}
                placeholder="--"
                min="0"
                step="0.1"
              />
            </div>
            <div className="form-group">
              <label>Fat (g)</label>
              <input
                type="number"
                value={form.fat}
                onChange={(e) => setField('fat', e.target.value)}
                placeholder="--"
                min="0"
                step="0.1"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Fiber (g)</label>
            <input
              type="number"
              value={form.fiber}
              onChange={(e) => setField('fiber', e.target.value)}
              placeholder="--"
              min="0"
              step="0.1"
            />
          </div>

          <div className="settings-macro-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label>Serving Size</label>
              <input
                type="number"
                value={form.serving_size}
                onChange={(e) => setField('serving_size', e.target.value)}
                placeholder="e.g. 100"
                min="0"
                step="0.1"
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Unit</label>
              <input
                type="text"
                value={form.serving_unit}
                onChange={(e) => setField('serving_unit', e.target.value)}
                placeholder="g"
              />
            </div>
          </div>

          <button
            className="btn btn-primary btn-full"
            onClick={handleSubmit}
            disabled={saving}
            style={{ marginTop: '0.5rem' }}
          >
            {saving ? 'Saving...' : editingId ? 'Update Food' : 'Create Food'}
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Food List */}
      {!loading && foods.length === 0 && !showForm && (
        <div className="custom-foods-empty card">
          <p className="text-muted">No custom foods yet.</p>
          <p className="text-muted">Create your own foods to quickly log homemade meals and recipes.</p>
        </div>
      )}

      {!loading && foods.length > 0 && (
        <div className="custom-foods-list">
          {foods.map(food => (
            <div key={food.id} className="custom-food-item card">
              <div className="custom-food-info">
                <span className="custom-food-name">{food.food_name}</span>
                <div className="custom-food-meta">
                  <span className="custom-food-cal">{food.calories} cal</span>
                  {food.serving_size && (
                    <span className="text-muted"> per {food.serving_size}{food.serving_unit || 'g'}</span>
                  )}
                </div>
                <div className="custom-food-macros">
                  {food.protein && <span>P: {food.protein}g</span>}
                  {food.carbs && <span>C: {food.carbs}g</span>}
                  {food.fat && <span>F: {food.fat}g</span>}
                </div>
              </div>
              <div className="custom-food-actions">
                <button className="custom-food-btn edit" onClick={() => handleEdit(food)} title="Edit">
                  &#9998;
                </button>
                <button
                  className="custom-food-btn delete"
                  onClick={() => handleDelete(food.id)}
                  disabled={deletingId === food.id}
                  title="Delete"
                >
                  &#10005;
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default CustomFoods;
